import { useMemo } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronsUpDown,
  KeyRound,
  LayoutDashboard,
  LogOut,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import {
  useAuth,
  useIdentity,
  useRelationships,
} from '@/features/auth/hooks/useAuth';
import { useAuthActions } from '@/features/auth/hooks/useAuthActions';
import { groupWorkspaces, roleLabel } from '@/features/auth/model/memberships';
import { roleMessageKey } from '@/i18n/role';

export function WorkspaceAccountMenu({
  presentation,
}: {
  presentation: 'sidebar' | 'header';
}) {
  const { t } = useTranslation('shell');
  const { tenantId = '' } = useParams({ strict: false });
  const navigate = useNavigate();
  const { user } = useAuth();
  const { logout, isLoggingOut } = useAuthActions();
  const identityQuery = useIdentity();
  const relationshipsQuery = useRelationships();
  const workspaces = useMemo(
    () =>
      groupWorkspaces(
        identityQuery.data?.memberships ?? [],
        relationshipsQuery.data ?? [],
      ),
    [identityQuery.data, relationshipsQuery.data],
  );
  const displayName = user?.realName || user?.username || t('userFallback');
  const initials = displayName.slice(0, 1).toUpperCase();
  const translatedRole = (role: string): string => {
    const key = roleMessageKey(role);
    return key ? t(key) : roleLabel(role);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          presentation === 'sidebar' ? (
            <SidebarMenuButton
              size="lg"
              className="h-12 w-full rounded-lg data-[state=open]:bg-sidebar-accent"
            />
          ) : (
            <Button variant="ghost" size="icon-sm" aria-label={displayName} />
          )
        }
      >
        <Avatar className="size-8 rounded-full">
          <AvatarFallback className="rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {initials}
          </AvatarFallback>
        </Avatar>
        {presentation === 'sidebar' ? (
          <>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{displayName}</span>
              <span className="truncate text-xs">{user?.email}</span>
            </div>
            <ChevronsUpDown className="ml-auto size-4" aria-hidden="true" />
          </>
        ) : null}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-72 rounded-2xl p-2 shadow-(--shadow-card-hover)"
        align="end"
        side={presentation === 'sidebar' ? 'top' : 'bottom'}
        sideOffset={8}
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="px-3 py-2">
            <span className="block truncate text-sm">{displayName}</span>
            <span className="block truncate text-xs font-normal text-muted-foreground">
              {user?.email}
            </span>
          </DropdownMenuLabel>
        </DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="px-3 pb-1 pt-2 text-xs text-muted-foreground">
            {t('accountActions')}
          </DropdownMenuLabel>
          {workspaces.length > 1 ? (
            <DropdownMenuItem
              className="min-h-10 rounded-xl px-3"
              render={<Link to="/workspaces" />}
            >
              <LayoutDashboard aria-hidden="true" />
              {t('myWorkspaces')}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            className="min-h-10 rounded-xl px-3"
            render={<Link to="/invitations/accept" search={{}} />}
          >
            <KeyRound aria-hidden="true" />
            {t('useInvitationCode')}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="px-3 pb-1 pt-2 text-xs text-muted-foreground">
            {t('switchWorkspace')}
          </DropdownMenuLabel>
          {workspaces.map((workspace) => (
            <DropdownMenuItem
              key={workspace.tenantId}
              className="min-h-11 rounded-xl px-3"
              disabled={workspace.tenantId === tenantId}
              onClick={() => {
                if (workspace.tenantId === tenantId) return;
                void navigate({
                  to: '/w/$tenantId',
                  params: { tenantId: workspace.tenantId },
                });
              }}
            >
              <span className="grid size-7 shrink-0 place-items-center rounded-md bg-muted text-xs font-semibold">
                {workspace.name.slice(0, 1)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate">{workspace.name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {workspace.roles.map(translatedRole).join(' · ')}
                </span>
              </span>
              {workspace.tenantId === tenantId ? (
                <Check className="size-4 shrink-0" aria-hidden="true" />
              ) : null}
            </DropdownMenuItem>
          ))}
          {workspaces.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              {t('noWorkspaces')}
            </div>
          ) : null}
        </DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            className="min-h-10 rounded-xl px-3 focus:text-destructive"
            disabled={isLoggingOut}
            onClick={() => void logout()}
          >
            <LogOut aria-hidden="true" />
            {t('logout')}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
